import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Form,
  Select,
  Modal,
  message,
  Tag,
  Typography,
  Button,
} from "antd";
import { LinkOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  createAgentContainer,
  fetchAgentContainers,
} from "../../services/agentPortalService";
import {
  AGENT_CONTAINER_STATUSES,
  AGENT_ROUTES,
} from "../../config/agentPortal";
import { AGENT_TABLE_SCROLL_X } from "../../config/agentPortalLayout";
import {
  parseUrlInt,
  setUrlParams,
} from "../../utils/agentPortalUrlParams";
import {
  formatContainerStatusLabel,
  getContainerStatusColor,
} from "../../utils/containerStatusColors";
import { AgentContainersFilters } from "../../components/agent/AgentContainersFilters";
import { AgentAddToMasterModal } from "../../components/agent/AgentAddToMasterModal";
import { MasterContainerPortFields } from "../../components/containers/MasterContainerPortFields";
import { AgentPageHeader } from "../../components/agent/AgentPageHeader";
import { AgentListTabs } from "../../components/agent/AgentListTabs";
import { useAgentProfile } from "../../hooks/useAgentProfile";
import {
  getLinkedMastersFromContainer,
  type LinkedMasterSummary,
} from "../../utils/agentContainerMasterUtils";
import type { Container } from "../../types/booking";

const { Text } = Typography;

interface ContainerRow {
  id: number;
  created_at: string;
  status: string | null;
  reference: string | null;
  transport_type: number | null;
  origin_port: number | null;
  destination_port: number | null;
  etd: string | null;
  delayed_etd?: string | null;
  eta: string | null;
  track_and_trace: string | null;
  transport_type_info?: { id: number; name: string };
  origin_port_info?: { id: number; name: string };
  destination_port_info?: { id: number; name: string };
  master_container_info?: Container["master_container_info"];
  masters: LinkedMasterSummary[];
  primaryMaster: LinkedMasterSummary | null;
}

const CONTAINER_STATUS_KEYS = AGENT_CONTAINER_STATUSES.map((s) => s.value);

const AgentContainersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { agentId, agent } = useAgentProfile();
  const [containers, setContainers] = useState<ContainerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [transportFilter, setTransportFilter] = useState<number | undefined>(() =>
    parseUrlInt(searchParams.get("transport"))
  );
  const [statusFilter, setStatusFilter] = useState(() => {
    const value = searchParams.get("status");
    return value && CONTAINER_STATUS_KEYS.includes(value) ? value : "all";
  });
  const [originFilter, setOriginFilter] = useState<number | undefined>(() =>
    parseUrlInt(searchParams.get("origin"))
  );
  const [destinationFilter, setDestinationFilter] = useState<number | undefined>(
    () => parseUrlInt(searchParams.get("destination"))
  );
  const [masterFilter, setMasterFilter] = useState<number | "none" | undefined>(
    () => {
      const value = searchParams.get("master");
      if (value === "none") return "none";
      return parseUrlInt(value);
    }
  );
  const [groupByMaster, setGroupByMaster] = useState(
    () => searchParams.get("group") === "master"
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [transportTypes, setTransportTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [originPorts, setOriginPorts] = useState<{ id: number; name: string }[]>(
    []
  );
  const [destinationPorts, setDestinationPorts] = useState<
    { id: number; name: string }[]
  >([]);
  const [addToMasterContainer, setAddToMasterContainer] =
    useState<ContainerRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await fetchAgentContainers();
    if (error) message.error("Failed to load containers");
    const rows = (data || []).map((row) => {
      const container = row as ContainerRow;
      const masters = getLinkedMastersFromContainer(container);
      return {
        ...container,
        masters,
        primaryMaster: masters[0] ?? null,
      };
    });
    setContainers(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase
      .from("booking_transport_types")
      .select("id, name")
      .order("name")
      .then(({ data }) => setTransportTypes(data || []));
    supabase
      .from("booking_origin_port")
      .select("id, name")
      .order("name")
      .then(({ data }) => setOriginPorts(data || []));
    supabase
      .from("booking_destination_port")
      .select("id, name")
      .order("name")
      .then(({ data }) => setDestinationPorts(data || []));
  }, [load]);

  useEffect(() => {
    setUrlParams(setSearchParams, {
      q: search.trim() || undefined,
      transport: transportFilter,
      status: statusFilter === "all" ? undefined : statusFilter,
      origin: originFilter,
      destination: destinationFilter,
      master: masterFilter,
      group: groupByMaster ? "master" : undefined,
    });
  }, [
    search,
    transportFilter,
    statusFilter,
    originFilter,
    destinationFilter,
    masterFilter,
    groupByMaster,
    setSearchParams,
  ]);

  const masterOptions = useMemo(() => {
    const byId = new Map<number, { id: number; reference: string | null }>();
    for (const container of containers) {
      for (const master of container.masters) {
        if (!byId.has(master.id)) {
          byId.set(master.id, {
            id: master.id,
            reference: master.reference,
          });
        }
      }
    }
    return [...byId.values()].sort((a, b) =>
      (a.reference || "").localeCompare(b.reference || "")
    );
  }, [containers]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: containers.length };
    for (const status of CONTAINER_STATUS_KEYS) {
      counts[status] = 0;
    }
    for (const container of containers) {
      if (container.status && counts[container.status] != null) {
        counts[container.status] += 1;
      }
    }
    return counts;
  }, [containers]);

  const hasActiveFilters =
    !!search.trim() ||
    transportFilter != null ||
    originFilter != null ||
    destinationFilter != null ||
    masterFilter != null ||
    groupByMaster;

  const clearFilters = () => {
    setSearch("");
    setTransportFilter(undefined);
    setOriginFilter(undefined);
    setDestinationFilter(undefined);
    setMasterFilter(undefined);
    setGroupByMaster(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = containers.filter((c) => {
      if (transportFilter != null && c.transport_type !== transportFilter) {
        return false;
      }
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (originFilter != null && c.origin_port !== originFilter) return false;
      if (destinationFilter != null && c.destination_port !== destinationFilter) {
        return false;
      }
      if (masterFilter === "none") {
        if (c.masters.length > 0) return false;
      } else if (masterFilter != null) {
        if (!c.masters.some((m) => m.id === masterFilter)) return false;
      }
      if (!q) return true;
      const masterText = c.masters
        .map((m) => m.reference || `Master #${m.id}`)
        .join(" ")
        .toLowerCase();
      return (
        c.reference?.toLowerCase().includes(q) ||
        c.status?.toLowerCase().includes(q) ||
        c.transport_type_info?.name.toLowerCase().includes(q) ||
        c.origin_port_info?.name.toLowerCase().includes(q) ||
        c.destination_port_info?.name.toLowerCase().includes(q) ||
        masterText.includes(q)
      );
    });

    if (!groupByMaster) return rows;

    return [...rows].sort((a, b) => {
      const masterA = a.primaryMaster;
      const masterB = b.primaryMaster;
      if (!masterA && !masterB) {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
      if (!masterA) return 1;
      if (!masterB) return -1;
      const createdA = masterA.created_at
        ? new Date(masterA.created_at).getTime()
        : 0;
      const createdB = masterB.created_at
        ? new Date(masterB.created_at).getTime()
        : 0;
      if (createdB !== createdA) return createdB - createdA;
      if (masterA.id !== masterB.id) return masterB.id - masterA.id;
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [
    containers,
    search,
    transportFilter,
    statusFilter,
    originFilter,
    destinationFilter,
    masterFilter,
    groupByMaster,
  ]);

  const masterRowSpans = useMemo(() => {
    if (!groupByMaster) return new Map<number, number>();
    const spans = new Map<number, number>();
    let i = 0;
    while (i < filtered.length) {
      const masterId = filtered[i].primaryMaster?.id ?? -1;
      let count = 1;
      while (
        i + count < filtered.length &&
        (filtered[i + count].primaryMaster?.id ?? -1) === masterId
      ) {
        count += 1;
      }
      spans.set(filtered[i].id, count);
      for (let j = 1; j < count; j += 1) {
        spans.set(filtered[i + j].id, 0);
      }
      i += count;
    }
    return spans;
  }, [filtered, groupByMaster]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        reference: values.reference,
        transport_type: values.transport_type,
        etd: values.etd ? values.etd.format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? values.delayed_etd.format("YYYY-MM-DD")
          : null,
        eta: values.eta ? values.eta.format("YYYY-MM-DD") : null,
        track_and_trace: values.track_and_trace || null,
        origin_port: values.origin_port || null,
        destination_port: values.destination_port || null,
      };
      const { data, error } = await createAgentContainer(payload);
      if (error || !data) throw error;
      message.success("Container created");
      setModalOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      console.error(err);
      message.error("Failed to create container");
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<ContainerRow> = [
    {
      title: "Created",
      dataIndex: "created_at",
      render: (v: string) => dayjs(v).format("MMM D, YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: string) => (
        <Tag color={getContainerStatusColor(s)}>
          {formatContainerStatusLabel(s)}
        </Tag>
      ),
    },
    { title: "Reference", dataIndex: "reference" },
    {
      title: "Master BL/AWB",
      key: "master",
      width: 180,
      onCell: (row) => {
        if (!groupByMaster) return {};
        const span = masterRowSpans.get(row.id);
        return { rowSpan: span ?? 1 };
      },
      render: (_, row) => {
        if (row.masters.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setAddToMasterContainer(row);
              }}
            >
              Add to master
            </Button>
          );
        }
        return (
          <div>
            {row.masters.map((master) => (
              <div key={master.id}>
                <Text strong>{master.reference || `Master #${master.id}`}</Text>
                {master.created_at && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(master.created_at).format("MMM D, YYYY")}
                    </Text>
                  </div>
                )}
              </div>
            ))}
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setAddToMasterContainer(row);
              }}
              style={{ paddingInline: 0 }}
            >
              Change
            </Button>
          </div>
        );
      },
    },
    {
      title: "Transport",
      key: "transport",
      render: (_, row) => row.transport_type_info?.name || "-",
    },
    {
      title: "ETD",
      dataIndex: "etd",
      render: (_v: string, row) => {
        const effective = row.delayed_etd || row.etd;
        return effective ? dayjs(effective).format("MMM D, YYYY") : "-";
      },
      sorter: (a, b) => {
        const dateA = a.delayed_etd || a.etd;
        const dateB = b.delayed_etd || b.etd;
        const timeA = dateA ? new Date(dateA).getTime() : 0;
        const timeB = dateB ? new Date(dateB).getTime() : 0;
        return timeA - timeB;
      },
    },
    {
      title: "ETA",
      dataIndex: "eta",
      render: (v: string) => (v ? dayjs(v).format("MMM D, YYYY") : "-"),
      sorter: (a, b) => {
        const dateA = a.eta ? new Date(a.eta).getTime() : 0;
        const dateB = b.eta ? new Date(b.eta).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Origin",
      key: "origin",
      render: (_, row) => row.origin_port_info?.name || "-",
    },
    {
      title: "Destination",
      key: "dest",
      render: (_, row) => row.destination_port_info?.name || "-",
    },
  ];

  return (
    <div>
      <AgentPageHeader title="Containers" agentName={agent?.name} />

      <AgentListTabs
        activeKey={statusFilter}
        onChange={setStatusFilter}
        items={[
          { key: "all", label: "All", count: statusCounts.all },
          ...AGENT_CONTAINER_STATUSES.map((s) => ({
            key: s.value,
            label: s.label,
            count: statusCounts[s.value] ?? 0,
          })),
        ]}
      />

      <AgentContainersFilters
        search={search}
        transportFilter={transportFilter}
        originFilter={originFilter}
        destinationFilter={destinationFilter}
        masterFilter={masterFilter}
        groupByMaster={groupByMaster}
        transportTypes={transportTypes}
        originPorts={originPorts}
        destinationPorts={destinationPorts}
        masterOptions={masterOptions}
        hasActiveFilters={hasActiveFilters}
        resultCount={filtered.length}
        onSearchChange={setSearch}
        onTransportChange={setTransportFilter}
        onOriginChange={setOriginFilter}
        onDestinationChange={setDestinationFilter}
        onMasterChange={setMasterFilter}
        onGroupByMasterChange={setGroupByMaster}
        onClearFilters={clearFilters}
        onRefresh={load}
        onAddContainer={() => setModalOpen(true)}
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        scroll={{ x: AGENT_TABLE_SCROLL_X }}
        pagination={{
          pageSize: 100,
          showSizeChanger: true,
          responsive: true,
          showTotal: (total) => `${total} containers`,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement;
            if (target.closest("button") || target.closest("a")) return;
            navigate(AGENT_ROUTES.containerDetail(record.id));
          },
          style: { cursor: "pointer" },
        })}
      />

      <Modal
        title="Add container"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={submitting}
        okText="Add container"
        destroyOnClose
        width="min(560px, calc(100vw - 32px))"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Type"
            name="transport_type"
            rules={[{ required: true, message: "Select transport type" }]}
          >
            <Select
              options={transportTypes.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Reference"
            name="reference"
            rules={[{ required: true, message: "Reference is required" }]}
          >
            <Input placeholder="Container or AWB reference" />
          </Form.Item>
          <MasterContainerPortFields
            originPorts={originPorts}
            destinationPorts={destinationPorts}
            required
          />
        </Form>
      </Modal>

      {agentId != null && addToMasterContainer && (
        <AgentAddToMasterModal
          open={!!addToMasterContainer}
          agentId={agentId}
          containerId={addToMasterContainer.id}
          containerReference={addToMasterContainer.reference}
          transportTypeId={addToMasterContainer.transport_type}
          transportTypeName={addToMasterContainer.transport_type_info?.name}
          onClose={() => setAddToMasterContainer(null)}
          onSuccess={() => {
            setAddToMasterContainer(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default AgentContainersPage;
